import { useEffect, useLayoutEffect, useRef, type PointerEvent } from 'react';
import { createPortal } from 'react-dom';
import styles from './Toast.module.css';

export interface ToastProps {
  message: string;
  action?: { label: string; run: () => void };
  /** ms; default 4 s with an action, 2.5 s without (DESIGN_SYSTEM §3) */
  duration?: number;
  onDismiss: () => void;
}

export const TOAST_MS_ACTION = 4000;
export const TOAST_MS_INFO = 2500;
const SWIPE_PX = 24;

/** How long a toast stays: 4 s with an action, 2.5 s without, unless the caller says otherwise. */
export const toastDuration = (t: { action?: unknown; duration?: number }): number =>
  t.duration ?? (t.action ? TOAST_MS_ACTION : TOAST_MS_INFO);

/**
 * Past-tense result ("Kit saved"), optional Undo (DESIGN_SYSTEM §3). Tapping the toast closes it;
 * swipe down is a shortcut. The action runs and then closes it.
 */
export function Toast({ message, action, duration, onDismiss }: ToastProps) {
  const ms = toastDuration({ action, duration });
  const dismiss = useRef(onDismiss);
  useEffect(() => { dismiss.current = onDismiss; }, [onDismiss]);
  const swipe = useRef<number | null>(null);

  useEffect(() => {
    if (!Number.isFinite(ms)) return;
    const t = setTimeout(() => dismiss.current(), ms);
    return () => clearTimeout(t);
  }, [ms, message]);

  const onPointerDown = (e: PointerEvent) => { swipe.current = e.clientY; };
  const onPointerMove = (e: PointerEvent) => {
    if (swipe.current !== null && e.clientY - swipe.current > SWIPE_PX) {
      swipe.current = null;
      dismiss.current();
    }
  };

  return (
    <div className={styles.toast} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={() => { swipe.current = null; }}>
      <button type="button" className={styles.message} onClick={() => dismiss.current()} aria-label={`Dismiss: ${message}`}>
        {message}
      </button>
      {action && (
        <button
          type="button"
          className={styles.action}
          onClick={() => {
            action.run();
            dismiss.current();
          }}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

/** Px above the bottom of the viewport that keeps clear of the nav and of the screen's fixed bottom actions. */
export function toastClearance(): number {
  const vh = window.innerHeight;
  const nav = document.querySelector('nav[aria-label="Main"]')?.getBoundingClientRect();
  let top = nav && nav.height > 0 ? nav.top : vh;
  // Bottom actions count when they sit on the bottom edge of the free area (sticky above the nav).
  for (const el of document.querySelectorAll('[data-bottom-actions]')) {
    const r = el.getBoundingClientRect();
    if (r.height > 0 && r.bottom >= top - 24 && r.top < vh) top = Math.min(top, r.top);
  }
  return Math.max(0, vh - top);
}

/**
 * Live region that is always mounted so screen readers hear new toasts. Rendered on <body>, outside the
 * app root, so its actions stay tappable while a bottom sheet makes the app inert. While a toast shows,
 * it measures the screen's bottom actions every frame and sits above them (DESIGN_SYSTEM §3), or at the top while a sheet is open.
 */
export function ToastRegion({ children, active }: { children: React.ReactNode; active?: boolean }) {
  const host = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!active) return;
    let raf = 0;
    const place = () => {
      const el = host.current;
      if (el) {
        // An open sheet owns the bottom of the screen: the toast moves to the top edge so it never covers a sheet's buttons
        if (document.querySelector('[role="dialog"][aria-modal="true"]')) {
          el.style.bottom = 'auto';
          el.style.top = 'calc(env(safe-area-inset-top) + 8px)';
        } else {
          el.style.top = '';
          el.style.bottom = `${toastClearance() + 8}px`;
        }
      }
      raf = requestAnimationFrame(place);
    };
    place();
    return () => cancelAnimationFrame(raf);
  }, [active]);
  return createPortal(
    <div ref={host} className={styles.host} role="status" aria-live="polite">
      {children}
    </div>,
    document.body,
  );
}
